import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import RoofEditor2D from "../components/solar/RoofEditor2D";
import SceneErrorBoundary from "../components/solar/SceneErrorBoundary";
import FluxPreview from "../components/solar/FluxPreview";
import { CoordinateCard } from "../components/solar/CoordinateCard";
import { RelocateMap } from "../components/solar/RelocateMap";
import { Sun, Search, Loader2, Zap, Save, ArrowLeft, Flame, AlertTriangle, Map, FileDown, Share2, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

// Google Static Maps satellite è CAPPATO a zoom 21 (zoom 22/23/24 restituiscono l'immagine
// identica). Chiedere 22 e calcolare lo span come zoom 22 disallineava la satellitare (~2×).
const ZOOM_2D = 21;   // massima risoluzione raster reale di Google (≈36 px/m con scale=2)
// Google Static Maps limita la base a 640px: la copertura a terra è 640·mpp
function groundSizeMeters(lat, zoom = ZOOM_2D) {
  return 640 * (156543.03392 * Math.cos(lat * Math.PI / 180)) / Math.pow(2, zoom);
}

export default function Progetto3DEditor() {
  const { pid } = useParams();
  const [params] = useSearchParams();
  return <ProgettoFVEditor key={pid} pid={pid === "nuovo" ? null : pid} clienteId={params.get("cliente_id") || null} />;
}

export function ProgettoFVEditor({ pid: pidProp = null, clienteId: clienteProp = null, embedded = false, initialName, initialAddress = "", initialLat = null, initialLng = null, onSaved }) {
  const navigate = useNavigate();
  const [pid, setPid] = useState(pidProp);
  const isNew = !pid;
  const [sending, setSending] = useState(false);
  const snapRef = useRef(false);

  const [name, setName] = useState(initialName || "Nuovo progetto");
  const [query, setQuery] = useState(initialAddress);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [address, setAddress] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [insights, setInsights] = useState(null);
  const [sat2d, setSat2d] = useState(null);
  const [aerialCov, setAerialCov] = useState(null);
  const [layers, setLayers] = useState(null);
  const [viewMode, setViewMode] = useState("irr"); // "irr" heatmap irraggiamento · "2d" traccia impianto

  const [modules, setModules] = useState([]);
  const [inverters, setInverters] = useState([]);
  const [moduleId, setModuleId] = useState("");
  const [inverterId, setInverterId] = useState("");
  const [panelCount, setPanelCount] = useState(0);
  const [pvgis, setPvgis] = useState(null);
  const [sizing, setSizing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [clienteId, setClienteId] = useState(clienteProp);
  const [relocOpen, setRelocOpen] = useState(false);
  const [manualPanels, setManualPanels] = useState([]);
  const [roofRot, setRoofRot] = useState(0);
  const [roofPolys, setRoofPolys] = useState([]);
  const [shareUrl, setShareUrl] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/solar/catalog");
        const mods = data.filter((x) => x.type === "module");
        const invs = data.filter((x) => x.type === "inverter");
        setModules(mods); setInverters(invs);
        if (mods[0]) setModuleId(mods[0].id);
        if (invs[0]) setInverterId(invs[0].id);
      } catch { /* */ }
    })();
    if (!isNew) loadProject();
    else if (embedded) autoLocate();
    // eslint-disable-next-line
  }, []);

  const loadProject = async () => {
    try {
      const { data } = await api.get(`/solar/projects/${pid}`);
      setName(data.name); setAddress(data.address || ""); setClienteId(data.cliente_id || null);
      if (data.config?.module_id) setModuleId(data.config.module_id);
      if (data.config?.inverter_id) setInverterId(data.config.inverter_id);
      if (data.lat && data.lng) { setLat(data.lat); setLng(data.lng); await analyze(data.lat, data.lng, data.config?.total_panels); }
      // ripristina il layout salvato DOPO analyze (che azzera moduli/falde)
      if (data.config?.roof_polys?.length) setRoofPolys(data.config.roof_polys);
      if (data.config?.manual_panels?.length) setManualPanels(data.config.manual_panels);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const autoLocate = async () => {
    if (initialLat != null && initialLng != null) {
      setLat(initialLat); setLng(initialLng); setAddress(initialAddress);
      analyze(initialLat, initialLng);
      return;
    }
    if (!initialAddress.trim()) return;
    try {
      const { data } = await api.get("/solar/geocode", { params: { q: initialAddress } });
      if (data.results?.length) pickResult(data.results[0]);
      else toast.error("Indirizzo del cliente non trovato: cercalo a mano o inserisci le coordinate");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const doSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.get("/solar/geocode", { params: { q: query } });
      setResults(data.results || []);
      if (!data.results?.length) toast.error("Indirizzo non trovato");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSearching(false); }
  };

  const pickResult = (r) => {
    setResults([]); setQuery(r.display_name); setAddress(r.display_name);
    setLat(r.lat); setLng(r.lng);
    analyze(r.lat, r.lng);
  };

  const analyze = async (la, ln, presetPanels) => {
    setAnalyzing(true); setInsights(null); setLayers(null);
    setSat2d(null); setAerialCov(null); setManualPanels([]); setRoofPolys([]);
    try {
      const bi = await api.get("/solar/building-insights", { params: { lat: la, lng: ln } });
      setInsights(bi.data);
      const configs = bi.data.solarPotential?.solarPanelConfigs || [];
      const defCount = presetPanels ?? (configs.length ? configs[Math.floor(configs.length / 2)].panelsCount : 0);
      setPanelCount(defCount);
      try {
        // Foto aerea Google Solar (funziona anche dove satellite Static Maps è bloccato in EEA)
        const av = await api.get("/solar/aerial", { params: { lat: bi.data.center.latitude, lng: bi.data.center.longitude, radius: 30 } });
        setSat2d(av.data.image); setAerialCov(av.data.coverage_m);
      } catch {
        // fallback: Static Maps satellite (bloccato in area EEA) — non bloccante
        try {
          const sat2 = await api.get("/solar/static-map", { params: { lat: bi.data.center.latitude, lng: bi.data.center.longitude, zoom: ZOOM_2D, size: "640x640" }, responseType: "blob" });
          setSat2d(URL.createObjectURL(sat2.data));
        } catch { /* vista 2D non bloccante */ }
      }
      try {
        const dl = await api.get("/solar/data-layers", { params: { lat: bi.data.center.latitude, lng: bi.data.center.longitude, radiusMeters: 45, pixelSizeMeters: 0.1 } });
        setLayers(dl.data);
      } catch { setLayers(null); }
      return true;
    } catch (err) {
      toast.error(err?.response?.status === 404 ? "Nessun tetto rilevato qui. Prova un indirizzo più preciso (con numero civico)." : formatApiError(err));
      return false;
    } finally { setAnalyzing(false); }
  };

  const applyCoords = (la, ln) => {
    toast.info("Analizzo l'edificio alle coordinate indicate…");
    setLat(la); setLng(ln); analyze(la, ln);
  };

  const handlePickMeters = ({ x, z }) => {
    if (!insights?.center) return;
    const lat0 = insights.center.latitude, lng0 = insights.center.longitude;
    relocate(lat0 - z / 111320, lng0 + x / (Math.cos((lat0 * Math.PI) / 180) * 111320));
  };

  const relocate = (la, ln, syncCliente = false) => {
    toast.info("Analizzo l'edificio selezionato…");
    setLat(la); setLng(ln);
    analyze(la, ln).then((ok) => {
      if (!ok || !clienteId) return;
      if (syncCliente) {
        api.put(`/solar/cliente-coords/${clienteId}`, { lat: la, lng: ln })
          .then(() => toast.success("Coordinate aggiornate anche nella scheda cliente"))
          .catch((err) => toast.error(formatApiError(err)));
      }
      if (!embedded || snapRef.current) return;
      snapRef.current = true;
      api.post(`/solar/sopralluogo/${clienteId}/geo-snapshot`, { lat: la, lng: ln })
        .then(({ data }) => {
          if (data.skipped) toast.warning("Geolocalizzazione già completata: immagini non aggiornate");
          else if (data.saved) { toast.success("Edificio e irraggiamento salvati in Geolocalizzazione"); onSaved?.(pid, true); }
        })
        .catch((err) => toast.error(formatApiError(err)))
        .finally(() => { snapRef.current = false; });
    });
  };

  const sp = insights?.solarPotential;
  const configs = sp?.solarPanelConfigs || [];
  const chosenConfig = useMemo(() => {
    if (!configs.length) return null;
    let best = configs[0];
    for (const c of configs) { if (c.panelsCount <= panelCount) best = c; }
    return best;
  }, [configs, panelCount]);

  const selModule = modules.find((m) => m.id === moduleId);
  const manualActive = manualPanels.length > 0;
  const effCount = manualActive ? manualPanels.length : panelCount;
  const kwp = selModule ? Math.round((effCount * selModule.wp) / 10) / 100 : (sp ? Math.round((effCount * sp.panelCapacityWatts) / 10) / 100 : 0);
  const domSegment = useMemo(() => {
    const segs = sp?.roofSegmentStats || [];
    return [...segs].sort((a, b) => (b.stats?.areaMeters2 || 0) - (a.stats?.areaMeters2 || 0))[0];
  }, [sp]);
  const panelAz = domSegment?.azimuthDegrees ?? 180;
  const panelPitch = domSegment?.pitchDegrees ?? 20;

  // Le falde NON vengono più generate automaticamente: l'utente le disegna manualmente (tool "Disegna falda").

  const runPvgis = async () => {
    if (!lat || !lng || !kwp) return;
    try {
      const { data } = await api.get("/solar/pvgis", { params: { lat, lng, peakpower: kwp, angle: domSegment?.pitchDegrees ?? 30, azimuth: domSegment?.azimuthDegrees ?? 180 } });
      setPvgis(data);
      toast.success("Produzione stimata (PVGIS)");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const runSizing = async () => {
    if (!moduleId || !inverterId || !effCount) return;
    try {
      const { data } = await api.post("/solar/string-sizing", { module_id: moduleId, inverter_id: inverterId, total_panels: effCount });
      setSizing(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const persist = async () => {
    const config = {
      module_id: moduleId, inverter_id: inverterId, total_panels: effCount, kwp,
      manual_panels: manualPanels, roof_polys: roofPolys,
      tilt: domSegment?.pitchDegrees, azimuth: domSegment?.azimuthDegrees,
      center_lat: insights?.center?.latitude, center_lng: insights?.center?.longitude,
      panel_w: sp?.panelWidthMeters || 1.05, panel_h: sp?.panelHeightMeters || 1.9,
      google_yearly_kwh: chosenConfig?.yearlyEnergyDcKwh ? Math.round(chosenConfig.yearlyEnergyDcKwh) : null,
      pvgis, sizing,
    };
    const body = { name, address, lat, lng, cliente_id: clienteId, commessa_id: null, config };
    if (isNew) {
      const { data } = await api.post("/solar/projects", body);
      setPid(data.id);
      if (embedded) onSaved?.(data.id);
      else navigate(`/progetti-3d/${data.id}`, { replace: true });
      return data.id;
    }
    await api.put(`/solar/projects/${pid}`, body);
    return pid;
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Inserisci un nome"); return; }
    setSaving(true);
    try {
      await persist();
      toast.success(isNew ? "Progetto salvato" : "Progetto aggiornato");
      if (embedded && !isNew) onSaved?.(pid);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    if (isNew) { toast.error("Salva prima il progetto"); return; }
    setPdfBusy(true);
    try {
      await persist();
      const res = await api.get(`/solar/projects/${pid}/layout.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(formatApiError(err)); } finally { setPdfBusy(false); }
  };

  const createShare = async () => {
    if (isNew) { toast.error("Salva prima il progetto"); return; }
    setShareBusy(true);
    try {
      await persist();
      const { data } = await api.post(`/solar/projects/${pid}/share`);
      setShareUrl(data.public_url);
      try { await navigator.clipboard.writeText(data.public_url || ""); } catch { /* */ }
      toast.success(data.public_url ? "Link offerta copiato" : "Link creato");
    } catch (err) { toast.error(formatApiError(err)); } finally { setShareBusy(false); }
  };

  const sendToSopralluogo = async () => {
    if (!name.trim()) { toast.error("Inserisci un nome"); return; }
    setSending(true);
    try {
      const id = await persist();
      const { data } = await api.post(`/solar/projects/${id}/to-sopralluogo`);
      if (data.updated.length) toast.success(`Dati inseriti in: ${data.updated.join(", ")}`);
      if (data.skipped.length) toast.warning(`Sezioni già completate, non modificate: ${data.skipped.join(", ")}. Sbloccale per aggiornarle.`);
      onSaved?.(id, true);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSending(false); }
  };

  const g2d = aerialCov || (lat ? groundSizeMeters(lat, ZOOM_2D) : 60);

  return (
    <div data-testid="progetto-editor-page">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {!embedded && <button onClick={() => navigate("/progetti-3d")} className="p-2 rounded-md hover:bg-black/5" data-testid="editor-back"><ArrowLeft className="w-5 h-5" /></button>}
        <input value={name} onChange={(e) => setName(e.target.value)} className="text-xl md:text-2xl font-extrabold bg-transparent outline-none flex-1 min-w-0" style={{ fontFamily: "Manrope" }} data-testid="editor-name" />
        <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50" data-testid="editor-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva
        </button>
        {embedded && clienteId && (
          <button onClick={sendToSopralluogo} disabled={sending || !insights} className="btn-primary disabled:opacity-50" style={{ background: "#0F4A17" }} data-testid="editor-to-sopralluogo">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />} Inserisci nel sopralluogo
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Irraggiamento / 2D */}
        <div className="lg:col-span-2">
          {(lat || insights?.center) && (
            <div className="flex justify-end mb-2">
              <button onClick={() => setRelocOpen(true)} className="btn-secondary" data-testid="relocate-open"><Map className="w-4 h-4" /> Trova la casa sulla mappa</button>
            </div>
          )}
          {relocOpen && <RelocateMap lat={lat || insights.center.latitude} lng={lng || insights.center.longitude} onClose={() => setRelocOpen(false)}
            onConfirm={(la, ln) => { setRelocOpen(false); setRoofPolys([]); setManualPanels([]); relocate(la, ln, true); }} />}
          <div className="card-shell p-0 overflow-hidden relative h-[420px] md:h-[520px]" style={{ background: "#0b1020" }}>
            {analyzing ? (
              <div className="h-full flex items-center justify-center text-white text-sm"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Analisi tetto in corso…</div>
            ) : !insights ? (
              <div className="h-full flex flex-col items-center justify-center text-white/70 text-sm gap-2">
                <Sun className="w-10 h-10" />
                Cerca un indirizzo per iniziare la progettazione
              </div>
            ) : (
              <>
              <div className={viewMode === "irr" ? "h-full" : "hidden"}>
                {layers?.annualFluxUrl ? (
                  <div className="h-full overflow-auto p-3 flex items-start justify-center" data-testid="irr-view"><div className="w-full max-w-[480px]"><FluxPreview annualFluxUrl={layers.annualFluxUrl} /></div></div>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-white/70 text-sm gap-2 p-6 text-center" data-testid="irr-unavailable">
                    <Flame className="w-8 h-8" /> Mappa di irraggiamento non disponibile per questa posizione. Usa la vista 2D.
                  </div>
                )}
              </div>
              {viewMode === "2d" && (
              <SceneErrorBoundary>
                <RoofEditor2D
                  imageUrl={sat2d}
                  span={g2d}
                  panelW={sp?.panelWidthMeters || 1.05}
                  panelH={sp?.panelHeightMeters || 1.9}
                  moduleWp={selModule?.wp || sp?.panelCapacityWatts || 0}
                  domPitch={panelPitch}
                  maxPanels={panelCount}
                  polys={roofPolys}
                  setPolys={setRoofPolys}
                  panels={manualPanels}
                  setPanels={setManualPanels}
                  onPickBuilding={handlePickMeters}
                  rotation={roofRot}
                  onRotationChange={setRoofRot}
                  faldaAz={domSegment?.azimuthDegrees}
                  lat={lat || insights?.center?.latitude}
                  lng={lng || insights?.center?.longitude}
                />
              </SceneErrorBoundary>
              )}
              </>
            )}
          </div>
          {insights && !analyzing && (
            <>
              <div className="flex flex-wrap gap-2 mt-2 items-center">
                <button onClick={() => setViewMode("irr")} className={viewMode === "irr" ? "btn-primary" : "btn-secondary"} data-testid="view-heatmap"><Flame className="w-4 h-4" /> Irraggiamento</button>
                <button onClick={() => setViewMode("2d")} className={viewMode === "2d" ? "btn-primary" : "btn-secondary"} data-testid="view-2d"><Map className="w-4 h-4" /> 2D · Traccia impianto</button>
              </div>
              {viewMode === "irr" && (
                <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>Irraggiamento solare annuo sul tetto (Google Solar): zone più chiare = più sole, più produzione.</div>
              )}
              {viewMode === "2d" && (
                <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>Usa la rotazione (pulsante bussola) per mettere la falda dritta, poi "Disegna falda" o "Modifica bordi" per adattarla (trascina i pallini, clicca un lato per aggiungere un punto, doppio clic per rimuoverlo). "Posiziona"/"Riempi" inseriscono i moduli solo dentro il contorno. Misure lati e area mostrate in tempo reale.</div>
              )}
            </>
          )}
        </div>

        {/* Sidebar controlli */}
        <div className="space-y-4">
          <div className="card-shell p-4">
            <label className="text-xs font-semibold block mb-1" style={{ color: "var(--text-muted)" }}>Indirizzo</label>
            <div className="flex gap-2">
              <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") doSearch(); }}
                placeholder="Via, città…" className="flex-1 px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)" }} data-testid="address-input" />
              <button onClick={doSearch} disabled={searching} className="btn-secondary" data-testid="address-search">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </button>
            </div>
            {results.length > 0 && (
              <div className="mt-2 border rounded-md overflow-hidden" style={{ borderColor: "var(--border)" }} data-testid="address-results">
                {results.map((r, i) => (
                  <button key={i} onClick={() => pickResult(r)} className="block w-full text-left text-xs px-3 py-2 hover:bg-black/5 border-b last:border-0" style={{ borderColor: "var(--border)" }} data-testid={`address-result-${i}`}>
                    {r.display_name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <CoordinateCard lat={lat} lng={lng} onApply={applyCoords} />

          {sp && (
            <>
              <div className="card-shell p-4" data-testid="roof-summary">
                <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>Tetto rilevato {insights._requestedQuality && `(${insights._requestedQuality})`}</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Falde: <b>{sp.roofSegmentStats?.length || 0}</b></div>
                  <div>Max moduli: <b>{sp.maxArrayPanelsCount}</b></div>
                  <div>Inclinazione: <b>{Math.round(domSegment?.pitchDegrees || 0)}°</b></div>
                  <div>Orientamento: <b>{Math.round(domSegment?.azimuthDegrees || 0)}°</b></div>
                </div>
              </div>

              <div className="card-shell p-4">
                <label className="text-xs font-semibold block mb-2" style={{ color: "var(--text-muted)" }}>Numero moduli: <b style={{ color: "var(--primary)" }}>{panelCount}</b></label>
                <input type="range" min={0} max={sp.maxArrayPanelsCount} value={panelCount} onChange={(e) => setPanelCount(Number(e.target.value))} className="w-full" data-testid="panel-slider" />
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <div>
                    <label className="text-xs block mb-1" style={{ color: "var(--text-muted)" }}>Modulo</label>
                    <select value={moduleId} onChange={(e) => setModuleId(e.target.value)} className="w-full px-2 py-1.5 text-sm rounded-md border" style={{ borderColor: "var(--border)", background: "#fff" }} data-testid="module-select">
                      {modules.map((m) => <option key={m.id} value={m.id}>{m.brand} {m.model}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs block mb-1" style={{ color: "var(--text-muted)" }}>Inverter</label>
                    <select value={inverterId} onChange={(e) => setInverterId(e.target.value)} className="w-full px-2 py-1.5 text-sm rounded-md border" style={{ borderColor: "var(--border)", background: "#fff" }} data-testid="inverter-select">
                      {inverters.map((m) => <option key={m.id} value={m.id}>{m.brand} {m.model}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-lg font-extrabold" style={{ color: "var(--primary)", fontFamily: "Manrope" }} data-testid="kwp-value">
                  <Zap className="w-5 h-5" /> {kwp} kWp
                </div>
                {chosenConfig?.yearlyEnergyDcKwh && (
                  <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>Produzione Google: ~{Math.round(chosenConfig.yearlyEnergyDcKwh).toLocaleString("it-IT")} kWh/anno</div>
                )}
              </div>

              <div className="card-shell p-4 space-y-2">
                <button onClick={runPvgis} className="btn-secondary w-full justify-center" data-testid="pvgis-btn">Stima produzione (PVGIS)</button>
                {pvgis && (
                  <div className="text-sm" data-testid="pvgis-result">
                    <div className="font-bold" style={{ color: "var(--primary)" }}>{pvgis.annual_kwh.toLocaleString("it-IT")} kWh/anno</div>
                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>Resa specifica: {pvgis.specific_yield} kWh/kWp</div>
                  </div>
                )}
                <button onClick={runSizing} className="btn-secondary w-full justify-center" data-testid="sizing-btn">Dimensiona stringhe</button>
                {sizing && (
                  <div className="text-sm" data-testid="sizing-result">
                    {sizing.ok ? (
                      <>
                        <div><b>{sizing.n_strings}</b> stringhe × <b>{sizing.panels_per_string}</b> moduli</div>
                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>Rapporto DC/AC: {sizing.dc_ac_ratio} · finestra {sizing.n_min_series}–{sizing.n_max_series} moduli/stringa</div>
                        {sizing.strings_warning && <div className="text-xs mt-1 flex items-center gap-1 text-amber-600"><AlertTriangle className="w-3.5 h-3.5" /> Troppe stringhe per l'inverter</div>}
                      </>
                    ) : (
                      <div className="text-xs flex items-center gap-1 text-amber-600"><AlertTriangle className="w-3.5 h-3.5" /> {sizing.message}</div>
                    )}
                  </div>
                )}
              </div>

              <div className="card-shell p-4 space-y-2" data-testid="offerta-card">
                <div className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>Estrazione impianto</div>
                <button onClick={downloadPdf} disabled={pdfBusy || isNew} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="offerta-pdf-btn">
                  {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Scarica PDF impianto
                </button>
                <button onClick={createShare} disabled={shareBusy || isNew} className="btn-secondary w-full justify-center disabled:opacity-50" data-testid="offerta-share-btn">
                  {shareBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Crea link condivisibile
                </button>
                {isNew && <div className="text-xs" style={{ color: "var(--text-muted)" }}>Salva prima il progetto per generare il PDF/link.</div>}
                {shareUrl && (
                  <div className="text-xs break-all rounded-md p-2" style={{ background: "var(--surface-muted)", color: "var(--primary)" }} data-testid="offerta-share-url">
                    <a href={shareUrl} target="_blank" rel="noreferrer" className="underline">{shareUrl}</a>
                  </div>
                )}
                <div className="text-xs" style={{ color: "var(--text-muted)" }}>PDF con disegno del layout, misure dei lati in cm e mq per falda.</div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
